import {
  getOrderQueryFn,
  type Order,
  type OrderToken,
  type SubmittedRequest,
} from '../graphql/query/getOrder';
import { queryClient } from '../queryClient';
import { QUERIES } from './constants';
import {
  type WorkflowData,
  type TaskConfig,
  type Tasks,
  type Workflow,
} from '../workflow/schemaTypes';
import { type ReasonData } from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import { type ResolutionData } from '../components/tasks/ResolutionTask/ResolutionTaskSchema';
import { type MethodData } from '../components/tasks/MethodTask/MethodTaskSchema';

/**
 * Generates a unique query key for React Query based on workflow data and selected task IDs.
 *
 * The function creates query keys that include identifiers from selected workflow items and
 * their associated task data. This helps in cache management by ensuring that queries are
 * properly invalidated when relevant data changes.
 *
 * @param baseQueryName - The base name for the query key
 * @param options - Configuration options for generating the query key
 * @param options.taskIds - Array of task types to extract identifiers from
 * @param options.workflowConfig - The workflow configuration containing task definitions
 * @param options.workflowData - The current workflow data containing task-specific data
 *
 * @returns An array of strings that forms a unique query key for React Query
 */

export const generateQueryKey = <T extends WorkflowData | null>(
  baseQueryName: string,
  options: {
    currentItemId?: string;
    taskIds: Extract<Tasks, 'reason' | 'resolution' | 'returnMethod'>[];
    workflowConfig: Workflow | null;
    workflowData: T;
  },
): string[] => {
  const { workflowData, workflowConfig, taskIds, currentItemId } = options;

  // If no items are selected or no workflow config exists, return just the base query name
  if (
    !workflowData?.items ||
    Object.keys(workflowData.items).length === 0 ||
    !workflowConfig
  ) {
    return [baseQueryName];
  }

  const currentItem = currentItemId && workflowData.items?.[currentItemId];

  // Create a map of task IDs to their configurations for quick lookup
  const taskConfigMap = new Map<Tasks, TaskConfig>();
  for (const step of workflowConfig.steps) {
    for (const taskConfig of step.tasks) {
      taskConfigMap.set(taskConfig.id, taskConfig);
    }
  }

  // Define type for the various task data structures
  type TaskData = unknown;

  /**
   * Mapping of task types to functions that extract unique identifiers from task data.
   * Each extractor returns an array of identifier strings that will be included in the query key.
   */
  const dataExtractors: Record<
    Extract<Tasks, 'reason' | 'resolution' | 'returnMethod'>,
    (data: TaskData) => string[]
  > = {
    // Extract identifiers from reason data
    reason: (data: TaskData) => {
      const identifiers: string[] = [];
      const reasonData = data as ReasonData['reason'];
      if (reasonData?.reasonId) identifiers.push(reasonData.reasonId);
      if (reasonData?.nestedReasonId)
        identifiers.push(reasonData.nestedReasonId);
      // Tree flow: the same system reasonId can sit under different nodes whose
      // node-bound settings differ, and the request varies by nodeId/domain
      // (buildTreeReasonInput). Key on them, or switching nodes reuses the prior
      // node's cached delivery/resolution/refund entry (staleTime is Infinity).
      if (reasonData?.treeSelection?.nodeId)
        identifiers.push(reasonData.treeSelection.nodeId);
      if (reasonData?.treeSelection?.domain)
        identifiers.push(reasonData.treeSelection.domain);
      return identifiers;
    },
    // Extract identifiers from resolution data
    resolution: (data: TaskData) => {
      const resolutionData = data as ResolutionData['resolution'];
      const exchangeItemData = resolutionData.exchangeItem
        ? [
            resolutionData.exchangeItem.productId,
            resolutionData.exchangeItem.variantId,
          ]
        : [];
      return resolutionData?.resolutionId
        ? [resolutionData.resolutionId, ...exchangeItemData]
        : [];
    },
    // Extract identifiers from return method data
    returnMethod: (data: TaskData) => {
      const methodData = data as MethodData['returnMethod'];
      return methodData?.type ? [methodData.type] : [];
    },
  };

  // Default extractor for unknown task types
  const defaultExtractor = () => [];

  // Array to store the generated query fragments
  const queryFragments: string[] = [];

  const itemsToMapThrough = currentItem
    ? [currentItem]
    : Object.values(workflowData.items);

  // Iterate through each selected item to generate item-specific query fragments
  for (const item of itemsToMapThrough) {
    const itemId = item.itemSelection?.id;
    if (!itemId) return [];
    // Start with the item ID and quantity as the first identifiers
    const identifiers: string[] = [
      itemId,
      `QTY:${item.itemSelection?.quantity}`,
    ];

    // Process each requested task ID
    for (const taskId of taskIds) {
      const taskConfig = taskConfigMap.get(taskId);
      // Determine if the task applies to the entire cart or individual items
      const taskScope = taskConfig?.scope || 'item';

      // Get the appropriate task data based on scope and data structure
      const taskData =
        taskScope === 'cart' && workflowData
          ? workflowData[taskId as keyof WorkflowData]
          : workflowData?.items?.[itemId]?.[taskId];

      // Skip if no data exists for this task
      if (!taskData) continue;

      // Select the appropriate extractor based on the task ID
      const extractor =
        taskId && taskId in dataExtractors
          ? dataExtractors[taskId]
          : defaultExtractor;

      // Extract identifiers and add them to the list
      const extractedIds = extractor(taskData);
      identifiers.push(...extractedIds);
    }

    // Only add a query fragment if we have more than just the item ID
    if (identifiers.length > 1) {
      queryFragments.push(identifiers.join('-'));
    }
  }

  // Return the base query name followed by item-specific fragments
  // If no fragments were generated, just return the base name
  return queryFragments.length > 0
    ? [baseQueryName, ...queryFragments]
    : [baseQueryName];
};

export const queryOrder = async ({
  email,
  orderNumber,
  zipCode,
  isGift,
  requestId,
}: {
  email: string | null;
  orderNumber: string;
  zipCode: string | null;
  isGift?: boolean;
  requestId: string | null;
}): Promise<
  { order: Order; submittedRequests: SubmittedRequest[] } | { error: string }
> => {
  const queryKey = [QUERIES.GET_ORDER];

  const orderTokens: OrderToken[] = [
    { key: 'ORDER_NUMBER', value: orderNumber },
    ...(email ? [{ key: 'EMAIL' as const, value: email }] : []),
    ...(zipCode ? [{ key: 'ZIPCODE' as const, value: zipCode }] : []),
  ];

  try {
    const data = await queryClient.ensureQueryData({
      queryKey,
      queryFn: () =>
        getOrderQueryFn({
          input: { orderTokens, isGift },
          skipEligibility: Boolean(requestId),
        }),
      retry: false,
    });

    return data;
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : 'Failed to fetch order data',
    };
  }
};
